import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { Icon } from '@/components/forge/Icon'
import { Eyebrow } from '@/components/forge/primitives'
import { FILE_PURPOSES, acceptFor, uploadFile } from '@/features/files/api'

const PHOTO = FILE_PURPOSES.logbookPhoto

/**
 * The photos attached to an entry.
 *
 * `photo_file_ids` on `LogBookEntryCreate` — a list of ids the server turns
 * into `LogBookAttachment` rows, moving each object out of the temp folder as
 * it does. Empty is a valid answer and the key still travels; see
 * `createLogbookEntry`.
 *
 * ── Uploaded on PICK, not on save ─────────────────────────────────────────
 * `EditProfileDialog` uploads its avatar at submit, and is right to: there is
 * one file, and holding it means one failure to report against one control.
 *
 * Here there are several, and batching them into the save has two costs a
 * person actually feels. A rejection — wrong type, too large, magic bytes that
 * do not match the declared type — arrives after they have finished writing,
 * attached to a Save that did nothing, with no way to tell which of four photos
 * was the problem. And the dialog sits still for however long the slowest of
 * them takes, with the note already typed.
 *
 * Uploading as each one is chosen puts the refusal beside the thumbnail that
 * caused it while the rest carry on. The cost is an orphaned object when
 * somebody abandons the dialog, and `features/files/api.js` says in its own
 * header that this is the designed-for case: an id that is never attached is a
 * harmless orphan rather than half a record.
 *
 * ── The in-flight count belongs to the PARENT ────────────────────────────
 * It was local state here, and that was a real bug rather than a tidiness
 * question: a photo still uploading is not yet a row in `photos`, so the
 * dialog's `photosSettled` check saw an empty list and left Save enabled.
 * Pressing it mid-upload sent the create with the photo missing — silently,
 * with no error anywhere, because an entry with no photos is perfectly valid.
 *
 * So the count is lifted and the dialog disables Save on it. A caller that
 * renders this field and ignores `onUploadingChange` is back to the same bug,
 * which is why the prop is not optional.
 *
 * ── The local check happens first ────────────────────────────────────────
 * `uploadFile` runs `checkFile` before it asks for a presigned address, so a
 * 12MB photo is refused here rather than after 12MB of it has been sent. The
 * server re-checks both the size and the actual bytes at confirm time and
 * remains the authority.
 */
export function LogbookPhotoField({ photos, onChange, uploading, onUploadingChange, disabled }) {
  const { t } = useTranslation(['app', 'common'])
  const inputRef = useRef(null)

  const add = async (event) => {
    const chosen = Array.from(event.target.files ?? [])
    // Cleared immediately, so choosing the same file twice in a row still fires
    // `change` — the input keeps its value otherwise and the second pick is silent.
    event.target.value = ''
    if (!chosen.length) return

    onUploadingChange((count) => count + chosen.length)

    /* Sequential rather than `Promise.all`. Four presigned PUTs at once against
       one bucket is not faster in any way a person notices, and a failure in
       the middle of a settled batch is harder to attribute to its file than one
       that stops where it happened. */
    for (const file of chosen) {
      /* The preview is a local object URL and never a data URI: a 5MB photo
         base64'd into React state is 6.7MB of string per thumbnail. Revoked
         when the row goes, in `remove`. */
      const previewUrl = URL.createObjectURL(file)
      try {
        const fileId = await uploadFile(file, PHOTO)
        onChange((current) => [...current, { fileId, name: file.name, previewUrl, error: null }])
      } catch (error) {
        onChange((current) => [
          ...current,
          {
            fileId: null,
            name: file.name,
            previewUrl,
            /* The reason, as a key rather than the server's sentence — the copy
               belongs to i18n. `rejected` is the confirm-time refusal, which
               arrives on a 200 and is turned into a throw by `uploadFile`. */
            error: error?.fields?.file ?? 'failed',
          },
        ])
      } finally {
        onUploadingChange((count) => count - 1)
      }
    }
  }

  const remove = (index) => {
    onChange((current) => {
      const going = current[index]
      if (going?.previewUrl) URL.revokeObjectURL(going.previewUrl)
      return current.filter((_, position) => position !== index)
    })
  }

  return (
    <div className="flex flex-col gap-2">
      <Eyebrow>{t('logbook.form.photos')}</Eyebrow>
      <p className="m-0 text-body-sm text-ink-dim">{t('logbook.form.photosHint')}</p>

      {photos.length ? (
        <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
          {photos.map((photo, index) => (
            <li
              key={`${photo.name}-${index}`}
              className="relative flex w-[7.5rem] flex-col gap-1 rounded-sm border border-hairline bg-sunken p-1.5"
            >
              <img
                src={photo.previewUrl}
                alt={photo.name}
                className="h-16 w-full rounded-xs object-cover"
              />
              <span className="truncate text-micro text-ink-dim" title={photo.name}>
                {photo.name}
              </span>

              {/* A failed upload STAYS on screen rather than vanishing. A row
                  that disappears looks like it was never picked, and the person
                  tries the same file again. It carries no `fileId`, so the
                  submit gate below refuses to save while one is present. */}
              {photo.error ? (
                <span className="text-micro text-on-red">
                  {t(`logbook.form.photoError.${photo.error}`, {
                    defaultValue: t('logbook.form.photoError.failed'),
                  })}
                </span>
              ) : null}

              <ForgeButton
                variant="ghost"
                size="sm"
                type="button"
                disabled={disabled}
                onClick={() => remove(index)}
                aria-label={t('logbook.form.photoRemove', { name: photo.name })}
                className="absolute end-1 top-1 h-6 bg-card px-1.5"
              >
                <Icon name="x" size="xs" />
              </ForgeButton>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          multiple
          // Only what the server will take, from `PURPOSE_RULES` rather than a
          // list restated here — see `features/files/api.js`.
          accept={acceptFor(PHOTO)}
          onChange={add}
          className="sr-only"
        />
        <ForgeButton
          type="button"
          size="sm"
          disabled={disabled || uploading > 0}
          onClick={() => inputRef.current?.click()}
        >
          <Icon name="plus" size="xs" />
          {t('logbook.form.photoAdd')}
        </ForgeButton>

        {uploading > 0 ? (
          <span className="text-body-sm text-ink-dim">
            {t('logbook.form.photoUploading', { count: uploading })}
          </span>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Whether the photos are in a state that can be saved.
 *
 * Two conditions, and the second is the one that is easy to forget: every row
 * that EXISTS must carry a file id, AND nothing may still be in flight. A photo
 * mid-upload is not in `photos` at all, so checking the list alone answers
 * "yes" about a list that is about to grow.
 */
export const photosSettled = (photos, uploading) =>
  uploading === 0 && photos.every((photo) => Boolean(photo.fileId))
